import { Calendar, Instagram, Menu, Phone, Send, User, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "../ui/button";
import { PHONE_TEL } from "@/mock";

const safeScrollToId = (id) => {
    if (typeof document === "undefined") return;
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export const Header = () => {
    const [isOpen, setIsOpen] = useState(false);
    const headerRef = useRef(null);
    const [headerHeight, setHeaderHeight] = useState(64);

    const navigate = useNavigate();
    const pathname = useLocation().pathname;

    useEffect(() => {
        const el = headerRef.current;
        const compute = () => {
            if (el) setHeaderHeight(Math.ceil(el.getBoundingClientRect().height));
        };
        compute();
        window.addEventListener("resize", compute);
        return () => window.removeEventListener("resize", compute);
    }, []);

    useEffect(() => {
        const onScroll = () => {
            const hdr = headerRef.current;
            if (!hdr) return;
            if (window.scrollY > 8) hdr.classList.add("header-compact");
            else hdr.classList.remove("header-compact");
        };
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const closeMenu = () => setIsOpen(false);
    const handleLinkClick = (href) => {
        if (pathname !== '/') {
            navigate('/')
            setTimeout(() => {
                safeScrollToId(href.replace("#", ""));
            }, 300);
        } else {
            safeScrollToId(href.replace("#", ""));
            closeMenu();
        }
    };
    const handlePhoneClick = () => closeMenu();

    const NavLink = ({ href, children }) => (
        <button
            type="button"
            className="px-3 py-2 text-sm text-olive-900/80 hover:text-olive-900 transition-colors w-full text-left md:w-auto md:text-center"
            onClick={() => handleLinkClick(href)}
        >
            {children}
        </button>
    );

    return (
        <header
            id="site-header"
            ref={headerRef}
            className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-border transition-[height,background-color] duration-300"
        >
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
                {/* logo */}
                <div className="flex items-center gap-3">
                    {/* <div className="w-9 h-9 rounded-full bg-terracotta-400/20 ring-1 ring-terracotta-300/50 grid place-items-center">
                        <Calendar className="w-4 h-4 text-olive-600" />
                    </div> */}
                    <Link
                        to="/"
                        className="text-xl text-olive-900 tracking-wide"
                        onClick={closeMenu}
                    >
                        <img src="/image/svg/logoWithText.svg" alt="cope.by" className="w-28 h-16" />
                    </Link>
                </div>

                {/* hamburger (mobile) */}
                <div className="flex items-center md:hidden">
                    <button
                        type="button"
                        className="p-2 text-olive-900"
                        onClick={() => setIsOpen((v) => !v)}
                        aria-label={isOpen ? "Закрыть меню" : "Открыть меню"}
                        aria-expanded={isOpen}
                    >
                        {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                    </button>
                </div>

                {/* desktop nav */}
                <nav className="hidden md:flex items-center">
                    <NavLink href="#hero">Обо мне</NavLink>
                    <NavLink href="#services">Услуги</NavLink>
                            <Link to="/articles" className="px-3 py-2 text-sm text-olive-900/80 hover:text-olive-900 transition-colors w-full text-left md:w-auto md:text-center" onClick={closeMenu}>Статьи</Link>
                    <NavLink href="#contacts">Контакты</NavLink>
                    <Link to="/tests" className="px-3 py-2 text-sm text-olive-900/80 hover:text-olive-900 transition-colors w-full text-left md:w-auto md:text-center">Тесты</Link>
                </nav>

                {/* action buttons */}
                <div className="hidden sm:flex items-center gap-2">
                    <Button
                        asChild
                        variant="outline"
                        className="text-primary hover:bg-primary/10 border border-primary/20 hover:border-transparent hover:text-primary"
                    >
                        <a href="https://t.me/bondarevapro" target="_blank" rel="noopener noreferrer">
                            <Send className="w-4 h-4" />
                        </a>
                    </Button>

                    <Button
                        asChild
                        variant="outline"
                        className="text-primary hover:bg-primary/10 border border-primary/20 hover:border-transparent hover:text-primary"
                    >
                        <a href={PHONE_TEL} onClick={handlePhoneClick}>
                            <Phone className="w-4 h-4" />
                        </a>
                    </Button>

                    <Button
                        asChild
                        variant="outline"
                        className="text-primary hover:bg-primary/10 border border-primary/20 hover:border-transparent hover:text-primary"
                    >
                        <a href="https://www.instagram.com/bondareva.pro/" target="_blank" rel="noopener noreferrer">
                            <Instagram className="w-4 h-4" />
                        </a>
                    </Button>
                    <Button
                        asChild
                        className="bg-olive-800 hover:bg-emerald-700 text-white"
                    >
                        <Link to="/profile">
                            <User className="w-4 h-4" />
                        </Link>
                    </Button>
                </div>
            </div>

            {/* mobile dropdown — позиционируем по высоте headerRef */}
            {
                isOpen && (
                    <div
                        className="md:hidden absolute left-0 right-0 bg-white border-b border-border shadow-lg pb-4"
                        style={{ top: `${headerHeight}px` }}
                    >
                        <nav className="flex flex-col px-4 space-y-2 pt-2">
                            <NavLink href="#hero">Обо мне</NavLink>
                            <NavLink href="#services">Услуги</NavLink>
                    <Link to="/articles" className="px-3 py-2 text-sm text-olive-900/80 hover:text-olive-900 transition-colors w-full text-left md:w-auto md:text-center">Статьи</Link>
                            <NavLink href="#contacts">Контакты</NavLink>
                            <Link to="/tests" className="px-3 py-2 text-sm text-olive-900/80 hover:text-olive-900 transition-colors w-full text-left md:w-auto md:text-center">Тесты</Link>

                            {/* ⭐ ИЗМЕНЕНИЕ: Горизонтальное расположение кнопок на мобильном (flex justify-center) */}
                            <div className="pt-4 flex justify-center gap-3 px-0">

                                <Button
                                    asChild
                                    variant="outline"
                                    className="text-primary hover:bg-primary/10 border border-primary/20 hover:border-transparent hover:text-primary"
                                >
                                    <a href="https://t.me/bondarevapro" target="_blank" rel="noopener noreferrer">
                                        <Send className="w-4 h-4" />
                                    </a>
                                </Button>

                                <Button
                                    asChild
                                    variant="outline"
                                    className="text-primary hover:bg-primary/10 border border-primary/20 hover:border-transparent hover:text-primary"
                                >
                                    <a href={PHONE_TEL} onClick={handlePhoneClick}>
                                        <Phone className="w-4 h-4" />
                                    </a>
                                </Button>

                                <Button
                                    asChild
                                    variant="outline"
                                    className="text-primary hover:bg-primary/10 border border-primary/20 hover:border-transparent hover:text-primary"
                                >
                                    <a href="https://www.instagram.com/bondareva.pro/" target="_blank" rel="noopener noreferrer">
                                        <Instagram className="w-4 h-4" />
                                    </a>
                                </Button>
                                <Button
                                    asChild
                                    className="bg-olive-800 hover:bg-emerald-700 text-white"
                                >
                                    <Link to="/profile">
                                        <User className="w-4 h-4" />
                                    </Link>
                                </Button>
                            </div>
                        </nav>
                    </div>
                )
            }
        </header >
    );
};
